import { useEffect, useRef, useState } from "react";
import type { TaskApi } from "./api";
import { formatDateTime } from "./format";
import type { Task, TaskComment } from "./types";

export function TaskComments({ task, api, members, canPost }: {
  task: Task;
  api: TaskApi;
  members: { id: string; displayName: string }[];
  canPost: boolean;
}) {
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");
  const submission = useRef<{ body: string; sourceId: string } | null>(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    let active = true;
    let reading = false;
    const load = async () => {
      if (reading) return;
      reading = true;
      try {
        const result = await api.listComments(task.id, task.workspace_id);
        if (active) {
          setComments(current => {
            const incoming = new Map(result.comments.map(comment => [comment.id, comment]));
            for (const comment of current) if (!incoming.has(comment.id)) incoming.set(comment.id, comment);
            return [...incoming.values()].sort((a, b) => a.created_at - b.created_at);
          });
          setError("");
        }
      } catch (e) {
        if (active) setError(e instanceof Error ? e.message : "コメントを取得できませんでした。");
      } finally {
        reading = false;
        if (active) setLoading(false);
      }
    };
    void load();
    const interval = window.setInterval(() => { if (!document.hidden) void load(); }, 30_000);
    const onVisible = () => { if (!document.hidden) void load(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      active = false;
      alive.current = false;
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [api, task.id, task.workspace_id]);

  const post = async () => {
    const text = body.trim();
    if (!text || posting || !canPost) return;
    if (submission.current?.body !== text) submission.current = { body: text, sourceId: `web:${crypto.randomUUID()}` };
    const pending = submission.current;
    setPosting(true);
    setError("");
    try {
      const result = await api.addComment(task.id, task.workspace_id, text, pending.sourceId);
      if (!alive.current) return;
      setComments(current => current.some(comment => comment.id === result.comment.id) ? current : [...current, result.comment]);
      setBody("");
      submission.current = null;
    } catch (e) {
      if (alive.current) setError(e instanceof Error ? e.message : "コメントを投稿できませんでした。");
    } finally {
      if (alive.current) setPosting(false);
    }
  };

  return <section aria-label="相談・対応の経過" className="flex flex-col gap-3 rounded-md border border-border-default bg-surface-base p-3">
    <h3 className="text-sm font-semibold text-fg-primary">相談・対応の経過</h3>
    {loading && <p className="text-xs text-fg-muted">コメントを読み込んでいます…</p>}
    {!loading && comments.length === 0 && !error && <p className="text-xs text-fg-muted">コメントはまだありません。</p>}
    <ol className="flex flex-col gap-3" aria-live="polite" aria-relevant="additions">
      {comments.map(comment => <li key={comment.id} className="rounded-md bg-canvas p-3">
        <div className="mb-1 flex flex-wrap items-center justify-between gap-2 text-xs text-fg-muted">
          <span className="font-medium text-fg-secondary">{comment.source_kind === "integration" ? "業務連携" : members.find(member => member.id === comment.user_id)?.displayName ?? "メンバー"}</span>
          <time dateTime={new Date(comment.created_at).toISOString()}>{formatDateTime(comment.created_at)}</time>
        </div>
        <p className="whitespace-pre-wrap break-words text-sm text-fg-primary">{comment.body_md}</p>
      </li>)}
    </ol>
    {error && <p role="alert" className="text-sm text-danger">{error}</p>}
    {canPost && <form onSubmit={event => { event.preventDefault(); void post(); }} className="flex flex-col gap-2">
      <label className="flex flex-col gap-1 text-xs text-fg-muted">
        コメント
        <textarea value={body} onChange={event => setBody(event.target.value)} disabled={posting} rows={3}
          placeholder="相談内容や対応の経過を記入してください"
          className="rounded-md border border-border-default bg-canvas px-3 py-2 text-sm text-fg-primary focus-visible:shadow-focus disabled:opacity-60" />
      </label>
      <button type="submit" disabled={posting || !body.trim()}
        className="self-end rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-fg-inverse hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50">
        {posting ? "投稿しています…" : "コメントを投稿"}
      </button>
    </form>}
  </section>;
}
